import React, { useEffect, useState } from 'react';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Grid,
  IconButton,
  List,
  ListItemButton,
  ListItemText,
  MenuItem,
  Paper,
  TextField,
  Typography,
} from '@mui/material';
import {
  getFanScanErrorMessage,
  getFanScanPayload,
  getFanScanQuestion,
  isFanScanApiSuccess,
  isFanScanAuthError,
  isFanScanConflict,
  listFanScanQuestions,
  updateFanScanQuestion,
  updateFanScanQuestionOrder,
  uploadFanScanBackground,
} from '../../services/fanScanApi';
import {
  PLACEHOLDER_NOTE,
  allowsHttpImageUrls,
  buildQuestionSaveBody,
  bundleIsEditable,
  collectQuestionErrors,
  PRESENTATIONS,
  aspectRatioError,
  backgroundColorError,
  fanScanUploadFileError,
  imageUrlError,
  presentationError,
  layoutHelp,
  layoutLabel,
  toQuestionDraft,
  validateQuestionOrder,
} from './fanScanUtils';

function updateAt(list, id, patch) {
  return (list || []).map((item) => (item.id === id ? { ...item, ...patch } : item));
}

function WeightFields({ weights, traits, disabled, onChange }) {
  return (
    <Box>
      <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
        Each weight is a whole number from 1 to 10, including 5. An option can set any number of traits, and any of those can be 6 or higher. Leave a field empty to leave that trait off. The same scale applies to each side of a preference pair.
      </Typography>
      <Grid container spacing={1}>
        {(traits || []).map((trait) => (
          <Grid item xs={6} sm={4} md={3} key={trait.key}>
            <TextField
              fullWidth
              size="small"
              label={trait.label || trait.key}
              value={weights?.[trait.key] ?? ''}
              disabled={disabled}
              onChange={(event) => onChange(trait.key, event.target.value)}
              inputProps={{ inputMode: 'numeric', 'aria-label': `${trait.label || trait.key} weight` }}
            />
          </Grid>
        ))}
      </Grid>
    </Box>
  );
}

function ImageUrlField({ label, value, disabled, allowHttp, onChange, onUpload }) {
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState('');
  const problem = imageUrlError(value, { allowHttp, subject: label });
  const preview = String(value || '').trim();

  const handleFile = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file || !onUpload) return;
    const fileError = fanScanUploadFileError(file);
    if (fileError) {
      setUploadError(fileError);
      return;
    }
    setUploading(true);
    setUploadError('');
    try {
      const url = await onUpload(file);
      if (url) onChange(url);
      else setUploadError('Upload did not return an image URL.');
    } catch (error) {
      setUploadError(error instanceof Error ? error.message : 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  return (
    <Box>
      <TextField
        fullWidth
        size="small"
        label={label}
        value={value}
        disabled={disabled || uploading}
        error={Boolean(problem || uploadError)}
        helperText={
          uploadError ||
          problem ||
          (uploading
            ? 'Uploading…'
            : 'Upload a PNG, JPEG, WEBP, or GIF up to 8MB, or paste a public URL. The upload only fills this field. Save the question to attach it. Clear the field to remove the image.')
        }
        onChange={(event) => onChange(event.target.value)}
        inputProps={{ 'aria-label': label }}
      />
      {onUpload ? (
        <Button
          component="label"
          variant="outlined"
          size="small"
          disabled={disabled || uploading}
          sx={{ mt: 1 }}
        >
          {uploading ? 'Uploading…' : 'Upload image'}
          <input
            hidden
            type="file"
            accept="image/png,image/jpeg,image/webp,image/gif"
            aria-label="Upload question background image"
            onChange={handleFile}
          />
        </Button>
      ) : null}
      {preview && !problem ? (
        <Box
          component="img"
          src={preview}
          alt=""
          sx={{ mt: 1, maxHeight: 120, maxWidth: '100%', objectFit: 'cover', display: 'block' }}
        />
      ) : null}
    </Box>
  );
}

function BackgroundColorField({ value, disabled, onChange }) {
  const problem = backgroundColorError(value);
  const current = String(value || '').trim();
  const pickerValue = /^#[0-9A-Fa-f]{6}$/.test(current) ? current : '#000000';
  return (
    <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start' }}>
      <TextField
        fullWidth
        size="small"
        label="Background color"
        value={value}
        disabled={disabled}
        error={Boolean(problem)}
        placeholder="#0A072C"
        helperText={
          problem ||
          'A # plus 6 hex digits, or a linear-gradient(...). Hex is saved in uppercase. A gradient is saved as typed. Clear the field to remove it. The image is separate.'
        }
        onChange={(event) => onChange(event.target.value)}
      />
      <Box
        component="input"
        type="color"
        aria-label="Background color picker"
        disabled={disabled}
        value={pickerValue}
        onChange={(event) => onChange(String(event.target.value || '').toUpperCase())}
        sx={{ width: 48, height: 40, mt: 0.5, p: 0.25, border: '1px solid', borderColor: 'divider', bgcolor: 'transparent' }}
      />
    </Box>
  );
}

function OptionEditor({ option, traits, disabled, allowHttp, showSticker, onChange }) {
  return (
    <Paper variant="outlined" sx={{ p: 2 }}>
      <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'ui-monospace, monospace' }}>
        {option.id}
        {option.position != null ? ` · position ${option.position}` : ''}
      </Typography>
      <TextField
        fullWidth
        size="small"
        label={showSticker ? 'Text (analytics fallback)' : 'Text'}
        value={option.text}
        disabled={disabled}
        multiline
        minRows={2}
        helperText={showSticker ? 'The app shows the sticker. Analytics uses this text, then the label, then the option id.' : undefined}
        onChange={(event) => onChange({ text: event.target.value })}
        sx={{ mt: 1, mb: 1.5 }}
      />
      {showSticker ? (
        <ImageUrlField
          label={`${option.id} sticker`}
          value={option.sticker_image_url}
          disabled={disabled}
          allowHttp={allowHttp}
          onChange={(sticker_image_url) => onChange({ sticker_image_url })}
        />
      ) : null}
      <Box sx={{ mt: 1.5 }}>
      <WeightFields
        weights={option.weights}
        traits={traits}
        disabled={disabled}
        onChange={(key, value) => onChange({ weights: { ...option.weights, [key]: value } })}
      />
      </Box>
    </Paper>
  );
}

function FanScanQuestions({ selectedId, onSelect, bundleStatus, onSaved }) {
  const [loadingList, setLoadingList] = useState(true);
  const [loadingQuestion, setLoadingQuestion] = useState(false);
  const [saving, setSaving] = useState(false);
  const [savingOrder, setSavingOrder] = useState(false);
  const [savedOrder, setSavedOrder] = useState([]);
  const [error, setError] = useState('');
  const [orderNotice, setOrderNotice] = useState('');
  const [formError, setFormError] = useState('');
  const [notice, setNotice] = useState('');
  const [traits, setTraits] = useState([]);
  const [questions, setQuestions] = useState([]);
  const [question, setQuestion] = useState(null);
  const [draft, setDraft] = useState(null);

  const editable = bundleIsEditable(bundleStatus);
  const allowHttp = allowsHttpImageUrls();
  const currentOrder = questions.map((item) => item.id);
  const orderDirty = savedOrder.length > 0 && currentOrder.join('|') !== savedOrder.join('|');

  useEffect(() => {
    let cancelled = false;
    setLoadingList(true);
    listFanScanQuestions().then((res) => {
      if (cancelled) return;
      if (isFanScanAuthError(res)) {
        window.location = '/login';
        return;
      }
      if (!isFanScanApiSuccess(res)) {
        setError(getFanScanErrorMessage(res, 'Failed to load questions.'));
        setLoadingList(false);
        return;
      }
      const data = getFanScanPayload(res) || {};
      const nextQuestions = data.questions || [];
      setTraits(data.traits || []);
      setQuestions(nextQuestions);
      setSavedOrder(nextQuestions.map((item) => item.id));
      setError('');
      setLoadingList(false);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!selectedId) {
      setQuestion(null);
      setDraft(null);
      setFormError('');
      return undefined;
    }
    let cancelled = false;
    setLoadingQuestion(true);
    setFormError('');
    setNotice('');
    getFanScanQuestion(selectedId).then((res) => {
      if (cancelled) return;
      setLoadingQuestion(false);
      if (isFanScanAuthError(res)) {
        window.location = '/login';
        return;
      }
      if (!isFanScanApiSuccess(res)) {
        setQuestion(null);
        setDraft(null);
        setFormError(
          res?.httpStatus === 404
            ? 'That question was not found.'
            : getFanScanErrorMessage(res, 'Failed to load this question.')
        );
        return;
      }
      const data = getFanScanPayload(res) || {};
      if (data.traits) setTraits(data.traits);
      setQuestion(data.question || null);
      setDraft(toQuestionDraft(data.question));
      setFormError('');
    });
    return () => {
      cancelled = true;
    };
  }, [selectedId]);

  const patchDraft = (patch) => setDraft((current) => ({ ...current, ...patch }));

  const patchOption = (id, patch) => {
    setDraft((current) => ({
      ...current,
      options: updateAt(current.options, id, patch),
    }));
  };

  const patchPair = (id, patch) => {
    setDraft((current) => ({
      ...current,
      pairs: updateAt(current.pairs, id, patch),
    }));
  };

  const moveQuestion = (index, direction) => {
    setQuestions((current) => {
      const nextIndex = index + direction;
      if (nextIndex < 0 || nextIndex >= current.length) return current;
      const copy = current.slice();
      const [item] = copy.splice(index, 1);
      copy.splice(nextIndex, 0, item);
      return copy.map((entry, orderIndex) => ({ ...entry, order: orderIndex + 1 }));
    });
  };

  const saveOrder = async () => {
    const ids = questions.map((item) => item.id);
    const problem = validateQuestionOrder(ids);
    if (problem) {
      setError(problem);
      return;
    }
    setSavingOrder(true);
    setError('');
    setOrderNotice('');
    const res = await updateFanScanQuestionOrder(ids);
    setSavingOrder(false);
    if (isFanScanAuthError(res)) {
      window.location = '/login';
      return;
    }
    if (!isFanScanApiSuccess(res)) {
      setError(
        isFanScanConflict(res)
          ? getFanScanErrorMessage(res, 'Approved bundles cannot be edited.')
          : getFanScanErrorMessage(res, 'Could not save the question order.')
      );
      return;
    }
    const data = getFanScanPayload(res) || {};
    const nextQuestions = data.questions || questions;
    if (data.traits) setTraits(data.traits);
    setQuestions(nextQuestions);
    setSavedOrder(nextQuestions.map((item) => item.id));
    setOrderNotice('Saved. New sessions use this order. A scan already in progress keeps the order it started with.');
    if (onSaved) onSaved();
  };

  const save = async () => {
    const errors = collectQuestionErrors(question, draft, traits, { allowHttp });
    if (errors.length) {
      setFormError(errors.join(' '));
      setNotice('');
      return;
    }
    const body = buildQuestionSaveBody(question, draft);
    if (!body) {
      setFormError('');
      setNotice('Nothing changed.');
      return;
    }
    setSaving(true);
    setFormError('');
    setNotice('');
    const res = await updateFanScanQuestion(question.id, body);
    setSaving(false);
    if (isFanScanAuthError(res)) {
      window.location = '/login';
      return;
    }
    if (!isFanScanApiSuccess(res)) {
      setFormError(
        isFanScanConflict(res)
          ? getFanScanErrorMessage(res, 'Approved bundles cannot be edited.')
          : getFanScanErrorMessage(res, 'Could not save this question.')
      );
      return;
    }
    const data = getFanScanPayload(res) || {};
    const saved = data.question || question;
    if (data.traits) setTraits(data.traits);
    setQuestion(saved);
    setDraft(toQuestionDraft(saved));
    setQuestions((current) => updateAt(current, saved.id, saved));
    setNotice('Saved. New sessions and later answers use this copy.');
    if (onSaved) onSaved(saved);
  };

  const preference = question?.layout_kind === 'pick_a_side';
  const listedQuestion = questions.find((item) => item.id === question?.id);
  const displayOrder = listedQuestion?.order ?? question?.order;

  return (
    <Grid container spacing={2}>
      <Grid item xs={12} md={4}>
        <Paper sx={{ p: 1 }}>
          {loadingList ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
              <CircularProgress size={28} />
            </Box>
          ) : (
            <>
              <Typography variant="caption" color="text.secondary" display="block" sx={{ px: 1, pt: 1 }}>
                A scan already in progress keeps the order it started with. A new session uses the saved order.
              </Typography>
              <List dense aria-label="Fan Scan questions">
                {questions.map((item, index) => (
                  <Box key={item.id} sx={{ display: 'flex', alignItems: 'center' }}>
                    <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                      <IconButton
                        size="small"
                        aria-label={`Move ${item.title} earlier`}
                        disabled={!editable || savingOrder || index === 0}
                        onClick={() => moveQuestion(index, -1)}
                      >
                        <KeyboardArrowUpIcon fontSize="small" />
                      </IconButton>
                      <IconButton
                        size="small"
                        aria-label={`Move ${item.title} later`}
                        disabled={!editable || savingOrder || index === questions.length - 1}
                        onClick={() => moveQuestion(index, 1)}
                      >
                        <KeyboardArrowDownIcon fontSize="small" />
                      </IconButton>
                    </Box>
                    <ListItemButton
                      selected={item.id === selectedId}
                      onClick={() => onSelect(item.id)}
                      sx={{ flex: 1 }}
                    >
                      <ListItemText
                        primary={`${item.order}. ${item.title}`}
                        secondary={`${item.id} · ${layoutLabel(item.layout_kind)}`}
                      />
                    </ListItemButton>
                  </Box>
                ))}
              </List>
              {orderDirty ? (
                <Box sx={{ px: 1, pb: 1 }}>
                  <Button variant="outlined" onClick={saveOrder} disabled={!editable || savingOrder}>
                    {savingOrder ? 'Saving order…' : 'Save order'}
                  </Button>
                </Box>
              ) : null}
            </>
          )}
          {orderNotice ? (
            <Alert severity="success" sx={{ m: 1 }}>
              {orderNotice}
            </Alert>
          ) : null}
          {error ? (
            <Alert severity="error" sx={{ m: 1 }}>
              {error}
            </Alert>
          ) : null}
        </Paper>
      </Grid>

      <Grid item xs={12} md={8}>
        {!selectedId ? (
          <Paper sx={{ p: 3 }}>
            <Typography>Choose a question to edit the copy players see.</Typography>
          </Paper>
        ) : loadingQuestion ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress size={28} />
          </Box>
        ) : (
          <Paper sx={{ p: 2 }}>
            {!editable ? (
              <Alert severity="warning" sx={{ mb: 2 }}>
                This bundle is {bundleStatus}, so questions cannot be edited. Only a draft can be saved.
              </Alert>
            ) : null}
            {formError ? (
              <Alert severity="error" sx={{ mb: 2 }}>
                {formError}
              </Alert>
            ) : null}
            {notice ? (
              <Alert severity="success" sx={{ mb: 2 }}>
                {notice}
              </Alert>
            ) : null}

            {question && draft ? (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <Box>
                  <Typography variant="h6">
                    {displayOrder}. {question.title}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {layoutHelp(question.layout_kind, question)} {PLACEHOLDER_NOTE}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                    Option ids stay fixed because answers already stored use them. Saving updates the live quiz.
                    Finished results keep the score they were given.
                  </Typography>
                </Box>

                <TextField
                  label="Title"
                  value={draft.title}
                  disabled={!editable}
                  onChange={(event) => patchDraft({ title: event.target.value })}
                />
                {question.layout_kind !== 'pick_a_side' ? (
                  <TextField
                    label="Context"
                    value={draft.context}
                    disabled={!editable}
                    multiline
                    minRows={2}
                    helperText="Clear this to remove the context line."
                    onChange={(event) => patchDraft({ context: event.target.value })}
                  />
                ) : null}
                <TextField
                  label="Prompt"
                  value={draft.prompt}
                  disabled={!editable}
                  multiline
                  minRows={3}
                  onChange={(event) => patchDraft({ prompt: event.target.value })}
                />
                <BackgroundColorField
                  value={draft.background_color}
                  disabled={!editable}
                  onChange={(background_color) => patchDraft({ background_color })}
                />
                <ImageUrlField
                  label="Question background image"
                  value={draft.background_image_url}
                  disabled={!editable}
                  allowHttp={allowHttp}
                  onUpload={async (file) => {
                    const res = await uploadFanScanBackground(file);
                    if (!isFanScanApiSuccess(res)) {
                      throw new Error(getFanScanErrorMessage(res, 'Upload failed'));
                    }
                    const data = getFanScanPayload(res) || {};
                    return data.image_url || data.imageUrl || '';
                  }}
                  onChange={(background_image_url) => {
                    const next = { background_image_url };
                    if (!String(background_image_url || '').trim()) {
                      next.aspect_ratio = '';
                      next.presentation = '';
                    }
                    patchDraft(next);
                  }}
                />
                {String(draft.background_image_url || '').trim() ? (
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        size="small"
                        label="Aspect ratio"
                        value={draft.aspect_ratio}
                        disabled={!editable}
                        placeholder="16:9"
                        error={Boolean(
                          aspectRatioError(draft.aspect_ratio, { hasImage: true }) ||
                            (question.aspect_ratio && !String(draft.aspect_ratio || '').trim())
                        )}
                        helperText={
                          aspectRatioError(draft.aspect_ratio, { hasImage: true }) ||
                          (question.aspect_ratio && !String(draft.aspect_ratio || '').trim()
                            ? 'Clear the background image to remove the aspect ratio.'
                            : '16:9, 9:16, 4:3, 3:2, 1:1, 5:4, or 4:5. A close decimal is saved as that pair. Blank on a new image is stored as 16:9.')
                        }
                        onChange={(event) => patchDraft({ aspect_ratio: event.target.value })}
                        inputProps={{ 'aria-label': 'Aspect ratio' }}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        select
                        fullWidth
                        size="small"
                        label="Presentation"
                        value={draft.presentation || 'backdrop'}
                        disabled={!editable}
                        error={Boolean(presentationError(draft.presentation, { hasImage: true }))}
                        helperText={
                          presentationError(draft.presentation, { hasImage: true }) ||
                          'backdrop, bleed, or hero. This is the question background, not a sticker. Blank on a new image is stored as backdrop.'
                        }
                        onChange={(event) => patchDraft({ presentation: event.target.value })}
                        inputProps={{ 'aria-label': 'Presentation' }}
                      >
                        {PRESENTATIONS.map((value) => (
                          <MenuItem key={value} value={value}>
                            {value}
                          </MenuItem>
                        ))}
                      </TextField>
                    </Grid>
                  </Grid>
                ) : null}
                <Typography variant="caption" color="text.secondary">
                  Upload fills the URL. Save the question to keep it. Color and image are independent. Clearing the image also clears the ratio and presentation.
                </Typography>
                {question.layout_kind === 'spectrum' ? (
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="Left label"
                        value={draft.left_label}
                        disabled={!editable}
                        onChange={(event) => patchDraft({ left_label: event.target.value })}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="Right label"
                        value={draft.right_label}
                        disabled={!editable}
                        onChange={(event) => patchDraft({ right_label: event.target.value })}
                      />
                    </Grid>
                  </Grid>
                ) : null}

                {preference
                  ? draft.pairs.map((pair) => (
                      <Box key={pair.id} sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                        <TextField
                          label={`Pair ${pair.id} label`}
                          value={pair.label}
                          disabled={!editable}
                          onChange={(event) => patchPair(pair.id, { label: event.target.value })}
                        />
                        <OptionEditor
                          option={pair.left}
                          traits={traits}
                          disabled={!editable}
                          onChange={(patch) => patchPair(pair.id, { left: { ...pair.left, ...patch } })}
                        />
                        <OptionEditor
                          option={pair.right}
                          traits={traits}
                          disabled={!editable}
                          onChange={(patch) => patchPair(pair.id, { right: { ...pair.right, ...patch } })}
                        />
                      </Box>
                    ))
                  : draft.options.map((option) => (
                      <OptionEditor
                        key={option.id}
                        option={option}
                        traits={traits}
                        allowHttp={allowHttp}
                        showSticker={question.layout_kind === 'reaction_grid'}
                        disabled={!editable}
                        onChange={(patch) => patchOption(option.id, patch)}
                      />
                    ))}

                <Box>
                  <Button variant="contained" onClick={save} disabled={!editable || saving}>
                    {saving ? 'Saving…' : 'Save question'}
                  </Button>
                </Box>
              </Box>
            ) : null}
          </Paper>
        )}
      </Grid>
    </Grid>
  );
}

export default FanScanQuestions;
